import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
// @ts-expect-error - imported from .js/.jsx file
import {ArchiveIcon} from 'shells/icon/svg/archive-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';
import {ListView} from 'shells/list-view/list-view';

import type {AudienceStub as Audience} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {AudienceListItem} from './audience-list-item';

type Props = {
    isLoading: boolean;
    isFiltering: boolean;
    isShowingArchived: boolean;
    audiences: Audience[];
    onDeleteAudience: (arg1: string) => Promise<any>;
    onRestoreAudience: (arg1: string) => Promise<any>;
    error: string | null | undefined;
    setIsAddingNewAudience: (arg1: boolean) => void;
};

export function AudiencesList(props: Props) {
    return (
        <ListView
            collection={props.audiences}
            itemSpacing={16}
            renderRow={(audience: Audience) => {
                const options = [];

                if (!audience.isArchived) {
                    options.push(
                        {
                            label: 'View',
                            handleSelect: () => {
                                appHistory.push(audience.htmlUrlPath);
                            },
                            icon: <EyeIcon wrapWithDiv={false} size={18} />,
                        },
                        {
                            label: `${
                                audience.membershipStats.totalCount.value > 0 ? 'Archive' : 'Delete'
                            }`,
                            variant: 'destructive',
                            handleSelect: () => {
                                props.onDeleteAudience(audience.id);
                            },
                            icon: <ArchiveIcon size={16} />,
                        }
                    );
                } else {
                    options.push({
                        label: 'Restore',
                        handleSelect: () => {
                            props.onRestoreAudience(audience.id);
                        },
                        icon: <ArchiveIcon size={16} />,
                    });
                }

                return (
                    <AudienceListItem
                        key={audience.id}
                        name={audience.name}
                        subscribersCount={
                            audience.membershipStats && audience.membershipStats.subscribedCount
                        }
                        isPublic={audience.isPublic}
                        deletedTime={audience.deletedTime}
                        htmlUrlPath={audience.htmlUrlPath}
                        // @ts-expect-error
                        actionMenuOptions={options}
                        lastContactedTime={audience.lastContactedTime}
                    />
                );
            }}
            isLoading={props.isLoading}
            loadingState={{
                spacing: 16,
                listPadding: 0,
                // @ts-expect-error
                barHeight: 91,
                backgroundColor: 'white',
            }}
            isError={Boolean(props.error)}
            errorState={{errorMessage: props.error}}
            isFiltering={props.isFiltering || props.isShowingArchived}
            nothingFoundState={{title: 'No audiences were found'}}
            emptyStateComponent={
                <EmptyStateLarge
                    title='You haven’t created any audiences yet!'
                    subtitle={
                        <span>
                            <p>
                                Once you create an audience and add members, you can message them
                                all at once by sending some outreach.
                            </p>
                            <p>
                                You can create an audience here or from your{' '}
                                {/* @ts-expect-error */}
                                <Link variant='primary' size='big' as={RouterLink} to='/people'>
                                    people list
                                </Link>
                                .
                            </p>
                        </span>
                    }
                    icon={
                        <div style={{width: 120, height: 120}}>
                            <Illustration src='cactus-empty-state' width='100%' height='100%' />
                        </div>
                    }
                    cta={
                        <Button
                            onClick={() => {
                                props.setIsAddingNewAudience(true);
                            }}
                            size='xlarge'
                            variant='primary'
                        >
                            Create your first audience
                        </Button>
                    }
                />
            }
        />
    );
}
